import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ApiClientError } from '../api/client';
import { AuthLayout } from '../components/AuthLayout';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useToast } from '../components/ui/ToastContainer';
import { useAuth } from '../auth/AuthContext';
import { consumeExpiredFlash } from '../lib/auth-events';
import {
  GoogleIdentityError,
  renderGoogleButton,
} from '../lib/google-identity';

interface LocationState {
  from?: { pathname?: string };
}

// オープンリダイレクト防止: location.state.from.pathname は通常 RequireAuth が
// 設定する内部 path だが、防御的に「絶対 URL / プロトコル相対 URL / "//" で始まる
// パス / 改行を含むもの」を弾き、純粋な内部 path のみ許可する。
function safeInternalPath(path: string | undefined, fallback: string): string {
  if (!path) return fallback;
  // 制御文字を含むものは拒否(改行・タブ等の URL 切替えトリック対策)
  if (/[\s\u0000-\u001f\u007f]/.test(path)) return fallback;
  // "/path" のみ許可。"//foo" や "/\\foo" はプロトコル相対扱いされる可能性があるので拒否
  if (!path.startsWith('/')) return fallback;
  if (path.startsWith('//') || path.startsWith('/\\')) return fallback;
  return path;
}

// Google ログインの 2要素フロー(設計書 §3.4.1 + CLAUDE.md「Google OAuth 2要素フロー拡張」)
// - 初回 Google ログインで「メール一致 / google_sub 未連携」だった場合、API は
//   `requires_link_confirmation` + link_token (5分) を返す。
// - その link_token を保持しつつパスワード入力フォームに切替えて、
//   `/v1/auth/google/confirm-link` で連携を確定する。
interface PendingGoogleLink {
  linkToken: string;
  email: string;
  // link_token の取得時刻 + link_expires_in からカウントダウンを表示するため
  expiresAtMs: number;
}

const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '').trim();

export function LoginPage() {
  const { login, loginWithGoogle, confirmGoogleLink, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const from = safeInternalPath(
    (location.state as LocationState | null)?.from?.pathname,
    '/dashboard',
  );

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // セッション期限切れ → /login にリダイレクトされた直後の 1 度だけトースト表示。
  // sessionStorage の flash は consume で削除されるので、リロードや別経路で
  // /login に来た場合は出ない。scope='performer' のフラッシュだけ消化し、
  // admin scope のフラッシュは /admin/login に温存する。
  useEffect(() => {
    if (consumeExpiredFlash('performer') === 'performer') {
      showToast(
        'セッションの有効期限が切れました。再度ログインしてください。',
        { level: 'warn' },
      );
    }
  }, [showToast]);

  // Google 関連
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const [googleReady, setGoogleReady] = useState(false);
  const [googleScriptError, setGoogleScriptError] = useState<string | null>(null);
  const [pendingLink, setPendingLink] = useState<PendingGoogleLink | null>(null);
  const [linkPassword, setLinkPassword] = useState('');
  const [linkSubmitting, setLinkSubmitting] = useState(false);

  // GIS スクリプトを読み込んで Google ボタンをレンダリングする。
  // GOOGLE_CLIENT_ID が未設定の場合は何もしない(ボタン非表示)。
  // pendingLink 中(パスワード入力フェーズ)はボタン領域を畳むのでアンマウントする。
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    if (pendingLink) return;
    const container = googleButtonRef.current;
    if (!container) return;

    let cleanup: (() => void) | null = null;
    let cancelled = false;

    void renderGoogleButton({
      clientId: GOOGLE_CLIENT_ID,
      container,
      buttonWidth: 304,
      onCredential: (idToken) => {
        if (cancelled) return;
        void handleGoogleCredential(idToken);
      },
      onError: (err) => {
        if (cancelled) return;
        setGoogleScriptError(err.message);
      },
    })
      .then((dispose) => {
        if (cancelled) {
          dispose();
          return;
        }
        cleanup = dispose;
        setGoogleReady(true);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof GoogleIdentityError
            ? err.message
            : 'Google ログインの初期化に失敗しました';
        setGoogleScriptError(message);
      });

    return () => {
      cancelled = true;
      cleanup?.();
    };
    // handleGoogleCredential はクロージャで最新の state を参照する必要があるが、
    // 内部で AuthContext のメソッドしか触っていないので依存に含めなくて良い。
    // pendingLink を依存に含めてフェーズ遷移時に再描画する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingLink]);

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  async function handleGoogleCredential(idToken: string) {
    setError(null);
    setInfo(null);
    try {
      const result = await loginWithGoogle(idToken);
      if (result.kind === 'tokens') {
        navigate(from, { replace: true });
        return;
      }
      // requires_link_confirmation: パスワード入力フェーズへ
      setPendingLink({
        linkToken: result.link_token,
        email: result.email,
        expiresAtMs: Date.now() + result.link_expires_in * 1000,
      });
      setLinkPassword('');
      setInfo(
        `${result.email} は既存アカウントと一致しました。連携を完了するには SafeLens のパスワードを入力してください。`,
      );
    } catch (err) {
      setError(formatGoogleLoginError(err));
    }
  }

  async function handleLinkConfirm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!pendingLink) return;
    setError(null);
    setLinkSubmitting(true);
    try {
      await confirmGoogleLink(pendingLink.linkToken, linkPassword);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.code === 'UNAUTHENTICATED') {
          // メッセージは API 側で「期限切れ」「パスワード違い」を分けている
          setError(err.message);
          // 期限切れの場合は最初からやり直し
          if (err.message.includes('期限切れ')) {
            setPendingLink(null);
          }
        } else if (err.code === 'FORBIDDEN') {
          setError(err.message);
        } else {
          setError(err.message);
        }
      } else {
        setError('連携の確定に失敗しました。時間を置いて再度お試しください。');
      }
    } finally {
      setLinkSubmitting(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatLoginError(err));
    } finally {
      setSubmitting(false);
    }
  }

  // ---- Render: パスワード連携確認フェーズ ----
  if (pendingLink) {
    return (
      <AuthLayout
        title="Google アカウントを連携"
        subtitle={`${pendingLink.email} のパスワードで連携を確定`}
        footer={
          <button
            type="button"
            onClick={() => {
              setPendingLink(null);
              setError(null);
              setInfo(null);
              setLinkPassword('');
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-accent)',
              cursor: 'pointer',
              fontSize: '11px',
              padding: 0,
            }}
          >
            ← ログイン画面に戻る
          </button>
        }
      >
        <form onSubmit={handleLinkConfirm}>
          {info && <InfoBox>{info}</InfoBox>}
          <Input
            label={`SafeLens のパスワード(${pendingLink.email})`}
            type="password"
            autoComplete="current-password"
            required
            value={linkPassword}
            onChange={(event) => setLinkPassword(event.target.value)}
            hint="登録済みのパスワードを入力すると Google アカウントが連携されます(リンクは 5 分間有効)"
          />
          {error && <ErrorBox>{error}</ErrorBox>}
          <Button type="submit" disabled={linkSubmitting}>
            {linkSubmitting ? '連携中…' : 'パスワードで連携を確定'}
          </Button>
        </form>
      </AuthLayout>
    );
  }

  // ---- Render: 通常ログインフェーズ ----
  return (
    <AuthLayout
      title="ログイン"
      subtitle="メールアドレスとパスワードでログイン"
      footer={
        <>
          アカウントをお持ちでない方は <Link to="/signup">登録申請</Link>
        </>
      }
    >
      {GOOGLE_CLIENT_ID && (
        <div style={{ marginBottom: '20px' }}>
          {/* GIS が描画するボタンのコンテナ。インライン背景はテーマと馴染ませる枠だけ */}
          <div
            ref={googleButtonRef}
            style={{
              minHeight: '44px',
              display: 'flex',
              justifyContent: 'center',
            }}
            aria-label="Google でログイン"
          />
          {!googleReady && !googleScriptError && (
            <p
              style={{
                margin: '6px 0 0',
                fontSize: '10px',
                color: 'var(--color-text-tertiary)',
                textAlign: 'center',
              }}
            >
              Google ログインを準備中…
            </p>
          )}
          {googleScriptError && (
            <p
              style={{
                margin: '6px 0 0',
                fontSize: '10px',
                color: 'var(--color-error-text)',
                textAlign: 'center',
              }}
            >
              {googleScriptError}
            </p>
          )}
          <div
            style={{
              marginTop: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '10px',
              color: 'var(--color-text-tertiary)',
            }}
          >
            <hr
              style={{
                flex: 1,
                border: 'none',
                borderTop: 'var(--border-default)',
              }}
            />
            <span>または</span>
            <hr
              style={{
                flex: 1,
                border: 'none',
                borderTop: 'var(--border-default)',
              }}
            />
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <Input
          label="メールアドレス"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Input
          label="パスワード"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        {info && <InfoBox>{info}</InfoBox>}
        {error && <ErrorBox>{error}</ErrorBox>}
        <Button type="submit" disabled={submitting}>
          {submitting ? 'ログイン中…' : 'ログイン'}
        </Button>
      </form>
    </AuthLayout>
  );
}

// ----------------------------------------------------------------
// 共通の表示ボックス
// ----------------------------------------------------------------

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        background: 'rgba(239, 68, 68, 0.12)',
        border: '0.5px solid rgba(239, 68, 68, 0.4)',
        color: 'var(--color-error-text)',
        borderRadius: 'var(--radius-md)',
        padding: '10px 12px',
        fontSize: '11px',
        marginBottom: '14px',
      }}
    >
      {children}
    </div>
  );
}

function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="status"
      style={{
        background: 'rgba(167, 139, 250, 0.10)',
        border: '0.5px solid rgba(167, 139, 250, 0.35)',
        color: 'var(--color-text-accent)',
        borderRadius: 'var(--radius-md)',
        padding: '10px 12px',
        fontSize: '11px',
        marginBottom: '14px',
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

// メール+パスワードログイン用のエラーメッセージマッピング。
// 設計書 §3.4.1 のエラーコードと、auth-service.ts loginPerformer の挙動に対応:
// - 401 UNAUTHENTICATED 「メールアドレスまたはパスワードが間違っています」
//   (メール存在の有無を露出させないため、認証情報不一致もアカウント未存在も同一)
// - 403 FORBIDDEN + details.status='pending'  → 承認待ち
// - 403 FORBIDDEN + details.status='disabled' → 凍結(details.reason があれば併記)
// - 429 RATE_LIMIT_EXCEEDED                  → 試行回数超過
// - 503 SERVICE_UNAVAILABLE                  → 一時的に利用不可
// - その他 / 通信エラー → 中立的な「失敗 + 承認待ちの可能性も併記」フォールバック
//   (ユーザの主訴: 承認待ちでログインを試した時に「失敗しました」だけ出て
//    原因が分からない、というケースをフォールバックでも軽減する)
export function formatLoginError(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.code === 'UNAUTHENTICATED') {
      return 'メールアドレスまたはパスワードが間違っています。';
    }
    if (err.code === 'FORBIDDEN') {
      const details = err.details as
        | { status?: string; reason?: string | null }
        | undefined;
      const status = details?.status;
      if (status === 'pending') {
        return '登録申請は受け付けましたが、まだ承認されていません。承認後にログイン可能になります。';
      }
      if (status === 'disabled') {
        const reason = details?.reason;
        return reason
          ? `このアカウントは無効化されています(理由: ${reason})。サポートまでお問い合わせください。`
          : 'このアカウントは無効化されています。サポートまでお問い合わせください。';
      }
      return err.message;
    }
    if (err.code === 'RATE_LIMIT_EXCEEDED') {
      return 'ログイン試行回数が多すぎます。しばらくしてから再度お試しください。';
    }
    if (err.code === 'SERVICE_UNAVAILABLE') {
      return 'ログインは現在一時的に利用できません。時間を置いて再度お試しください。';
    }
    if (err.code === 'VALIDATION_ERROR') {
      return err.message;
    }
    return err.message;
  }
  return 'ログインに失敗しました。時間を置いて再度お試しください。登録申請を出された方は、管理者の承認完了までお待ちください。';
}

// API のエラーメッセージから UX を考慮したテキストを返す。
// API 側 (auth-service.ts loginWithGoogleIdToken) のエラー文言:
// - 401 「Google ID Token が無効です」
// - 401 「Google アカウントのメールが未検証です」
// - 403 (status:pending) 「アカウントは管理者承認待ちです」
// - 403 (status:disabled) 「アカウントは無効化されています」
// - 401 「未登録のメールアドレスです。サインアップしてください。」
// - 503 「Google ログインは利用できません」
function formatGoogleLoginError(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.code === 'FORBIDDEN') {
      const status = (err.details as { status?: string } | undefined)?.status;
      if (status === 'pending') {
        return 'アカウントは管理者承認待ちです。承認後にログインしてください。';
      }
      if (status === 'disabled') {
        return 'アカウントは無効化されています。サポートまでお問い合わせください。';
      }
      return err.message;
    }
    if (err.code === 'UNAUTHENTICATED') {
      return err.message;
    }
    if (err.code === 'SERVICE_UNAVAILABLE') {
      return 'Google ログインは現在利用できません。メールアドレス + パスワードでログインしてください。';
    }
    return err.message;
  }
  return 'Google ログインに失敗しました。時間を置いて再度お試しください。';
}
